/** Small pieces the finance screens share: status pills, money, schedules in words. */
import { CircleCheck, CircleX, FileClock, Hourglass, OctagonX, PencilLine, TriangleAlert, CircleAlert } from "lucide-react";
import { useState } from "react";
import { StatusPill, type PillTone } from "@otii/ui/chip";
import { Input } from "@otii/ui/ui";
import { cn } from "@otii/ui/cn";
import { dayLabel, dayOfMonth, GATE_LABEL, LEVER_STATUS_LABEL, money, type GateState, type LeverStatus, type Repeat } from "@otii/gov-shared";

const GATE: Record<GateState, { tone: PillTone; icon: React.ReactNode }> = {
  green: { tone: "success", icon: <CircleCheck /> },
  amber: { tone: "warn", icon: <CircleAlert /> },
  red: { tone: "danger", icon: <TriangleAlert /> },
  stop: { tone: "stop", icon: <OctagonX /> },
};

export function GatePill({ state, compact, title }: { state: GateState; compact?: boolean; title?: string }) {
  const g = GATE[state];
  return (
    <StatusPill tone={g.tone} icon={g.icon} title={title} className={compact ? "px-1.5 text-[11px] leading-4" : undefined}>
      {GATE_LABEL[state]}
    </StatusPill>
  );
}

const STATUS: Record<LeverStatus, { tone: PillTone; icon: React.ReactNode }> = {
  draft: { tone: "neutral", icon: <PencilLine /> },
  proposed: { tone: "info", icon: <Hourglass /> },
  approved: { tone: "success", icon: <CircleCheck /> },
  not_approved: { tone: "danger", icon: <CircleX /> },
};

export function LeverStatusPill({ status }: { status: LeverStatus }) {
  const s = STATUS[status];
  return (
    <StatusPill tone={s.tone} icon={s.icon}>
      {LEVER_STATUS_LABEL[status]}
    </StatusPill>
  );
}

export function AssumedPill() {
  return (
    <StatusPill tone="neutral" icon={<FileClock />} title="Payment date assumed">
      Date assumed
    </StatusPill>
  );
}

/** Money with tabular figures; negative in the danger colour when asked. */
export function Money({ pence, sign, tone, exact, className }: { pence: number | null | undefined; sign?: boolean; tone?: boolean; exact?: boolean; className?: string }) {
  return <span className={cn("num whitespace-nowrap", tone && pence != null && pence < 0 && "text-danger-ink", className)}>{money(pence, { sign, exact })}</span>;
}

export { REPEAT_LABEL } from "@otii/gov-shared";

const ordinal = (n: number) => `${n}${n % 10 === 1 && n !== 11 ? "st" : n % 10 === 2 && n !== 12 ? "nd" : n % 10 === 3 && n !== 13 ? "rd" : "th"}`;

/** "£11,400 monthly on the 15th, 15 Nov 2026 – 15 Jan 2027". Amount as given (signed for levers). */
export function describeSchedule(s: { date: string; repeat: Repeat | null; until: string | null; amount: number }, signed = false): string {
  const amt = money(s.amount, { sign: signed });
  if (!s.repeat) return `${amt} on ${dayLabel(s.date)}`;
  const when = s.repeat === "week" ? "weekly" : s.repeat === "month" ? `monthly on the ${ordinal(dayOfMonth(s.date))}` : s.repeat === "quarter" ? `quarterly on the ${ordinal(dayOfMonth(s.date))}` : "yearly";
  return s.until ? `${amt} ${when}, ${dayLabel(s.date)} – ${dayLabel(s.until)}` : `${amt} ${when} from ${dayLabel(s.date)}`;
}

/** Pounds typed in, pence out. Accepts "11,400", "£11400.50". */
export function parsePounds(raw: string): number | null {
  const s = raw.replace(/[£,\s]/g, "");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}
const penceToText = (p: number | null) => (p === null ? "" : (p / 100).toLocaleString("en-GB", { minimumFractionDigits: p % 100 ? 2 : 0, maximumFractionDigits: 2 }));

export function MoneyInput({ value, onChange, invalid, id, placeholder, className, disabled, "aria-label": ariaLabel }: { value: number | null; onChange: (pence: number | null) => void; invalid?: boolean; id?: string; placeholder?: string; className?: string; disabled?: boolean; "aria-label"?: string }) {
  const [text, setText] = useState(() => penceToText(value));
  const [focused, setFocused] = useState(false);
  const shown = focused ? text : penceToText(value);
  return (
    <div className={cn("relative", className)}>
      <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-muted">£</span>
      <Input
        id={id}
        aria-label={ariaLabel}
        inputMode="decimal"
        className="num pl-7 text-right"
        invalid={invalid}
        disabled={disabled}
        placeholder={placeholder}
        value={shown}
        onFocus={() => {
          setText(penceToText(value));
          setFocused(true);
        }}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          setText(e.target.value);
          onChange(parsePounds(e.target.value));
        }}
      />
    </div>
  );
}

/** An on/off switch. */
export function Switch({ checked, onChange, disabled, label }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors disabled:cursor-not-allowed disabled:opacity-40",
        checked ? "border-olive bg-olive" : "border-line bg-cream-deep",
      )}
    >
      <span className={cn("inline-block size-3.5 rounded-full bg-paper shadow-sm transition-transform", checked ? "translate-x-[18px]" : "translate-x-[2px]")} />
    </button>
  );
}
